import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import * as monthUtils from '@actual-app/core/shared/months';
import { amountToInteger } from '@actual-app/core/shared/util';
import type { IntegerAmount } from '@actual-app/core/shared/util';

import { saldoApertura } from '#components/mobile/historia/historia';
import type { ExtractoLeido } from '#components/mobile/historia/leerExtractoHistoria';
import { rangoFechas } from '#components/mobile/importar/importar';
import type { ErrorLectura } from '#components/mobile/importar/leerArchivo';
import { Bocadillo } from '#components/mobile/travesia/Bocadillo';
import { Boton } from '#components/mobile/ui/Boton';
import { CampoImporte } from '#components/mobile/ui/CampoImporte';
import { IconoCaja } from '#components/mobile/ui/IconoCaja';
import { color } from '#components/mobile/ui/tokens';
import { useLocale } from '#hooks/useLocale';

import {
  estiloCampo,
  Etiqueta,
  HojaTravesia,
  TituloHoja,
  useEuros,
} from './comun';

type HojaExtractoProps = {
  leido: ExtractoLeido;
  /** Nombre de la cuenta del banco («CaixaBank»). */
  cuenta: string;
  ocupado?: boolean;
  bajando?: boolean;
  /** Elegir otro archivo. */
  onCambiar: () => void;
  /** Sin extracto: volver a escribir el saldo a mano. */
  onSinExtracto: () => void;
  /** Saldo de hoy confirmado (céntimos). */
  onConfirmar: (saldoHoy: IntegerAmount) => void;
};

/**
 * Paso 5, con extracto: «El saldo sale del extracto». Si el archivo trae el
 * saldo final, Gavi lo enseña y pregunta si es lo que ve en el banco; si no
 * lo trae (o la cifra no es), se escribe a mano. Con el saldo de hoy, Gavi
 * calcula hacia atrás con cuánto empezaba.
 */
export function HojaExtracto({
  leido,
  cuenta,
  ocupado,
  bajando,
  onCambiar,
  onSinExtracto,
  onConfirmar,
}: HojaExtractoProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const { eur } = useEuros();
  const [tecleado, setTecleado] = useState<number | null>(null);
  const [aMano, setAMano] = useState(false);

  if (leido.tipo === 'error') {
    const mensaje: Record<ErrorLectura, string> = {
      formato: t("I can't read that kind of file. Try CSV, Excel or Norma 43."),
      'excel-antiguo': t(
        'That Excel is the old kind. Open it and save it as .xlsx or CSV, or download it again in another format.',
      ),
      vacio: t('That file has no transactions I can recognise.'),
      ilegible: t(
        "I couldn't read that file. Try downloading it again, or in another format.",
      ),
    };
    return (
      <HojaTravesia
        aria-label={t('Where is your money today?')}
        data-testid="hoja-extracto"
        bajando={bajando}
        cabeza={
          <Bocadillo
            variante="hoja"
            texto={mensaje[leido.error]}
            estado="ladea"
            clave={1}
          />
        }
        pie={
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Boton bloque isDisabled={ocupado} onPress={onCambiar}>
              <Trans>Choose another file</Trans>
            </Boton>
            <Boton
              bloque
              variante="fantasma"
              isDisabled={ocupado}
              data-testid="extracto-a-mano"
              onPress={onSinExtracto}
            >
              <Trans>I type the balance</Trans>
            </Boton>
          </div>
        }
      >
        <TituloHoja>
          <Trans>Where is your money today?</Trans>
        </TituloHoja>
      </HojaTravesia>
    );
  }

  const { archivo, movimientos, saldoFinal } = leido;
  const rango = rangoFechas(movimientos);
  const dia = (fecha: string) => monthUtils.format(fecha, 'd MMM yyyy', locale);
  const diaCorto = (fecha: string) => monthUtils.format(fecha, 'd MMM', locale);
  const conCifra = saldoFinal != null && !aMano;
  const saldoHoy = conCifra ? saldoFinal : tecleado;
  const importes = movimientos.map(m => amountToInteger(m.importe));
  const entro = importes.filter(i => i > 0).reduce((a, i) => a + i, 0);
  const salio = importes.filter(i => i < 0).reduce((a, i) => a + i, 0);
  const apertura = saldoHoy == null ? null : saldoApertura(saldoHoy, importes);

  const textoGavi = conCifra
    ? rango
      ? t(
          'I read {{count}} transactions, from {{from}} to {{to}}. Look at the balance.',
          {
            count: movimientos.length,
            from: dia(rango.desde),
            to: diaCorto(rango.hasta),
          },
        )
      : ''
    : saldoFinal == null
      ? t(
          "Your file has {{count}} transactions, but it doesn't say how much you had. Tell me today's figure and I'll work out the rest.",
          { count: movimientos.length },
        )
      : t("Tell me today's figure and I'll work out the rest.");

  const filaCifra = (etiqueta: string, valor: string, testId?: string) => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 12,
        fontSize: 14,
        padding: '4px 0',
      }}
    >
      <span style={{ color: color.fg2 }}>{etiqueta}</span>
      <span
        data-testid={testId}
        style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
      >
        {valor}
      </span>
    </div>
  );

  return (
    <HojaTravesia
      aria-label={t('Where is your money today?')}
      data-testid="hoja-extracto"
      bajando={bajando}
      cabeza={
        <Bocadillo
          variante="hoja"
          texto={textoGavi}
          estado={conCifra ? 'aplaude' : 'vuela'}
          clave={conCifra ? 1 : 2}
        />
      }
      pie={
        conCifra ? (
          <div style={{ display: 'flex', gap: 8 }}>
            <Boton
              variante="fantasma"
              isDisabled={ocupado}
              data-testid="extracto-otra-cifra"
              onPress={() => setAMano(true)}
              style={{ flex: 1, minHeight: 54, borderRadius: 18 }}
            >
              <Trans>No, it's another figure</Trans>
            </Boton>
            <Boton
              isDisabled={ocupado}
              data-testid="extracto-es-eso"
              onPress={() => onConfirmar(saldoFinal)}
              style={{ flex: 1, minHeight: 54, borderRadius: 18 }}
            >
              <Trans>Yes, that's it</Trans>
            </Boton>
          </div>
        ) : (
          <Boton
            bloque
            isDisabled={ocupado || tecleado == null}
            data-testid="extracto-seguir"
            onPress={() => tecleado != null && onConfirmar(tecleado)}
          >
            <Trans>Continue</Trans>
          </Boton>
        )
      }
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '10px 12px',
          borderRadius: 16,
          backgroundColor: color.surface2,
          marginBottom: 14,
        }}
      >
        <IconoCaja tono="acento" size={36} icono="receipt" />
        <span style={{ flex: 1, minWidth: 0, lineHeight: 1.25 }}>
          <span
            data-testid="extracto-archivo"
            style={{
              display: 'block',
              fontWeight: 700,
              fontSize: 15,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {archivo}
          </span>
          {rango && (
            <span style={{ display: 'block', fontSize: 13, color: color.fg2 }}>
              {t('{{count}} transactions · from {{from}} to {{to}}', {
                count: movimientos.length,
                from: dia(rango.desde),
                to: dia(rango.hasta),
              })}
            </span>
          )}
        </span>
        <Boton
          variante="fantasma"
          isDisabled={ocupado}
          onPress={onCambiar}
          style={{ minHeight: 36, padding: '0 12px' }}
        >
          <Trans>Change</Trans>
        </Boton>
      </div>

      {conCifra ? (
        <div style={{ textAlign: 'center', margin: '6px 0 14px' }}>
          <div style={{ fontSize: 14, color: color.fg2, fontWeight: 600 }}>
            {t('Today you have in {{account}}', { account: cuenta })}
          </div>
          <div
            data-testid="extracto-saldo"
            style={{
              fontSize: 36,
              fontWeight: 800,
              letterSpacing: -0.5,
              fontVariantNumeric: 'tabular-nums',
              margin: '2px 0',
            }}
          >
            {eur(saldoFinal)}
          </div>
          {rango && (
            <div style={{ fontSize: 13, color: color.fg3 }}>
              {t('Final balance of the statement, {{date}}', {
                date: diaCorto(rango.hasta),
              })}
            </div>
          )}
          <TituloHoja>
            <Trans>Is it what you see in your bank?</Trans>
          </TituloHoja>
        </div>
      ) : (
        <div style={{ marginBottom: 12 }}>
          <Etiqueta htmlFor="extracto-saldo-hoy">
            <Trans>How much does your bank show today?</Trans>
          </Etiqueta>
          <CampoImporte
            id="extracto-saldo-hoy"
            aria-label={t('How much does your bank show today?')}
            data-testid="extracto-saldo-hoy"
            valor={tecleado}
            opcional
            conSigno
            onCambiar={setTecleado}
            style={{ ...estiloCampo, justifyContent: 'flex-start' }}
            estiloTexto={{ fontWeight: 800, fontSize: 20 }}
          />
          {rango && (
            <p style={{ fontSize: 13, color: color.fg2, margin: '8px 2px 0' }}>
              {t(
                "If there were transactions after {{date}} that aren't in the file, they come in later as always. Nothing changes.",
                { date: diaCorto(rango.hasta) },
              )}
            </p>
          )}
        </div>
      )}

      {apertura != null && rango && (
        <div
          data-testid="extracto-apertura"
          style={{
            borderRadius: 16,
            backgroundColor: color.surface2,
            padding: '10px 14px',
          }}
        >
          {filaCifra(
            t('You started on {{date}} with', { date: dia(rango.desde) }),
            eur(apertura),
          )}
          {filaCifra(t('Came in since then'), `+${eur(entro)}`)}
          {filaCifra(t('Went out since then'), eur(salio))}
          <p style={{ fontSize: 12, color: color.fg3, margin: '6px 0 0' }}>
            <Trans>
              I work it out backwards. Your budget starts with today's.
            </Trans>
          </p>
        </div>
      )}
    </HojaTravesia>
  );
}
